import { useEffect, useState, useRef } from "react";

/**
 * Custom hook to manage tags for campaign steps (Instagram, TikTok, etc.)
 * Handles tag state, validation, and form pre-submit logic
 *
 * @param {string} tagFieldName - The field name to use for tags in the form (e.g., 'instaTags', 'tags')
 * @returns {Object} An object containing:
 * @returns {Array} tags - Current array of tags
 * @returns {Function} setTags - Function to update tags state
 * @returns {React.RefObject} tagsInputComponent - Ref for the tags input component
 * @returns {Function} formPreSubmit - Handler to validate and process tags before form submission
 * @returns {Function} setExistingStep - Function to set an existing step for editing
 * @returns {Function} formReset - Function to reset form state and control form disabled state
 */
function useStepTags(tagFieldName = "tags") {
  const tagsInputComponent = useRef(null);
  const [tags, setTags] = useState([]);
  const [existingStep, setExistingStep] = useState(null);

  const formPreSubmit = selectedOption => {
    if (tags.length === 0) {
      tagsInputComponent.current.setTagsError(
        "At least one tag is required. Type something and then press enter/return"
      );
      throw Error("At least one tag is required.");
    }
    const selectedOptionValue = JSON.parse(selectedOption.value);
    selectedOptionValue[tagFieldName] = tags;
    selectedOption.value = JSON.stringify(selectedOptionValue);
    setTags([]);
  };

  useEffect(() => {
    if (existingStep) {
      setTags(existingStep[tagFieldName] || []);
      tagsInputComponent.current.setTagsError("");
    }
  }, [existingStep, tagFieldName]);

  const formReset = (shouldReset, onFormDisabledChange) => {
    if (shouldReset) {
      setTags([]);
      tagsInputComponent.current.setTypedTag("");
      tagsInputComponent.current.setTagsError("");
      setExistingStep(null);
    }
    onFormDisabledChange(!shouldReset);
  };

  return {
    tags,
    setTags,
    tagsInputComponent,
    formPreSubmit,
    setExistingStep,
    formReset,
  };
}

export default useStepTags;
